import {useState, useEffect} from "react";
import Color from "color";

const manageColours = (styles, coloursConfig, generateColours, updateStylesCallback) => {
    const [colours, setColours] = useState(coloursConfig);

    useEffect(() => {
        const mainColourKey = Object.keys(colours).find(key => colours[key]?.mainColour === true);
        const mainColour = styles[colours[mainColourKey].name];
        if (mainColour != null) {
            const generatedColours = generateColours(Color(mainColour));
            setColours(prevColours => {
                const updatedColours = { ...prevColours };
                Object.entries(generatedColours).forEach(([key, generatedColour]) => {
                    const styleColour = styles[prevColours[key].name];
                    if (styleColour != null && prevColours[key]?.autoGenerated) {
                        const isAutoGenerated = Color(styleColour).hex() === generatedColour.hex();
                        if (prevColours[key].autoGenerated !== isAutoGenerated) {
                            updatedColours[key] = {...prevColours[key], autoGenerated: isAutoGenerated}
                        }
                    }
                });
                return updatedColours;
            });
        }
    }, []);

    const updateColours = (type, newStyles) => {
        if (colours[type]?.mainColour) {
            mainColourUpdate(Object.values(newStyles)[0], newStyles);
        } else {
            if (Object.hasOwn(colours[type], 'autoGenerated')) {
                setColours(prevColours => ({
                    ...prevColours,
                    [type]: {...prevColours[type], autoGenerated: false}
                }));
            }
            updateStylesCallback(newStyles);
        }
    };

    const mainColourUpdate = (mainColour, newStyles) => {
        const generatedColours = generateColours(Color(mainColour));
        let genColours = {};
        Object.entries(colours).forEach(([key, colourProp]) => {
            if (colourProp?.autoGenerated && generatedColours[key]) genColours[colourProp.name] = generatedColours[key].hex();
        });
        updateStylesCallback({...newStyles, ...genColours});
    }

    const toggleGeneratedColours = (checked) => {
        setColours(prevColours => {
            const updatedColours = {...prevColours};
            Object.entries(prevColours).forEach(([key, colour]) => {
                if (Object.hasOwn(colour, 'autoGenerated')) {
                    updatedColours[key] = {
                        ...colour,
                        autoGenerated: checked
                    };
                }
            });
            return updatedColours;
        });
    }

    return {colours, updateColours, toggleGeneratedColours };
}

const getDefaultColour = (name, styles) => {
    if (styles[name] == null) {
        const contBaseElement = document.querySelector('.cont-base');
        const contBaseStyles = getComputedStyle(contBaseElement);
        const style = contBaseStyles.getPropertyValue(name);
        return style;
    } else {
        return styles[name];
    }
}

const getColourOnColour = (colour) => {
    const colourRGB = Color(colour).rgb().color;
    const luminance = calculateRelativeLuminance(colourRGB[0], colourRGB[1], colourRGB[2]);
    const isDark = luminance <= 0.5;
    return isDark ? Color('rgba(255, 255, 255, 1)') : Color('rgba(0, 0, 0, .87)');
}

const getRGBFromRGBA = (colour, a) => {
    const [r, g, b] = colour.rgb().color;
    const alpha = a;
    const white = 255;
    const red = Math.round((1 - alpha) * white + alpha * r);
    const green = Math.round((1 - alpha) * white + alpha * g);
    const blue = Math.round((1 - alpha) * white + alpha * b);
    return Color.rgb(red, green, blue);
}

const ensureColourContrast = (colour1, colour2) => {
    const [r1, g1, b1] = Color(colour1).rgb().color;
    const luminance1 = calculateRelativeLuminance(r1, g1, b1);

    const [r2, g2, b2] = Color(colour2).rgb().color;
    const luminance2 = calculateRelativeLuminance(r2, g2, b2);

    const contrastRatio = calculateContrastRatio(luminance1, luminance2);

    if (contrastRatio < 1.8) {
        let [h, s, l] = Color(colour1).hsl().color;

        s *= 1.2;
        s = Math.max(0, s);
        s = Math.min(100, s);

        l *= 0.8;
        l = Math.max(0, l);
        l = Math.min(100, l);
        // Recheck
        return ensureColourContrast(Color.hsl(h, s, l), colour2);
    } else {
        return colour1;
    }
}

const calculateRelativeLuminance = (r, g, b) => {
    const rsrgb = r / 255;
    const gsrgb = g / 255;
    const bsrgb = b / 255;

    const rlinear = rsrgb <= 0.03928 ? rsrgb / 12.92 : Math.pow((rsrgb + 0.055) / 1.055, 2.4);
    const glinear = gsrgb <= 0.03928 ? gsrgb / 12.92 : Math.pow((gsrgb + 0.055) / 1.055, 2.4);
    const blinear = bsrgb <= 0.03928 ? bsrgb / 12.92 : Math.pow((bsrgb + 0.055) / 1.055, 2.4);

    return 0.2126 * rlinear + 0.7152 * glinear + 0.0722 * blinear;
}

const calculateContrastRatio = (luminance1, luminance2) => {
    const lighter = Math.max(luminance1, luminance2);
    const darker = Math.min(luminance1, luminance2);

    return (lighter + 0.05) / (darker + 0.05);
}

export {
    manageColours,
    getDefaultColour,
    getColourOnColour,
    getRGBFromRGBA,
    ensureColourContrast,
    calculateRelativeLuminance,
    calculateContrastRatio
}